import { Component, StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import "./styles.css";

/**
 * Last-resort boundary. Before this, an unhandled render/commit error (e.g. the react-dom
 * "l is not a function" crash on chat switch) unmounted the whole tree to a blank white page.
 * Now we catch it, show a recoverable card, and surface the error text so a crash is diagnosable
 * from a single screenshot instead of a minified console dump.
 */
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error("[failover] app crashed", error, info.componentStack);
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="crash">
        <h2>Something broke</h2>
        <p className="muted">The app hit an error. Reloading usually fixes it.</p>
        <pre className="crash-detail">{this.state.error.message}</pre>
        <button onClick={() => location.reload()}>Reload</button>
      </div>
    );
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);
